/**
 * The record header's collection status: "Coletando" or "Pausada desde …", a switch that pauses
 * or resumes after a confirm naming the consequence, and archiving in a menu once paused.
 * The planner, not this control, stops a paused campaign's spend (platform design §5.1.1).
 * `ARCHIVED` is terminal, so it renders the status alone; a reader without write access sees the
 * status and no control.
 */
import { Ellipsis } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';

import { ConfirmDialog } from '../../../chrome/form/ConfirmDialog';
import { getLocale } from '../../../i18n';
import { Button } from '../../../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../ui/dropdown-menu';
import { Switch } from '../../../ui/switch';
import { cn } from '../../../ui/utils';
import { useSetCampaignState } from './api/queries';
import type { CampaignDetail } from './api/schemas';
import { rm } from './recordMessages';

type Dialog = 'pause' | 'resume' | 'archive' | null;

function statusText(campaign: CampaignDetail): string {
  if (campaign.state === 'ACTIVE') return rm('status.collecting');
  if (campaign.state === 'ARCHIVED') return rm('status.archived');
  if (campaign.paused_at == null) return rm('status.paused');
  const when = new Intl.DateTimeFormat(getLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(campaign.paused_at));
  return rm('status.pausedSince', { when });
}

export function CollectionStatus(props: {
  campaign: CampaignDetail;
  canWrite: boolean;
}): ReactNode {
  const { campaign } = props;
  const [dialog, setDialog] = useState<Dialog>(null);
  const mutation = useSetCampaignState(campaign.id);
  const switchId = useId();
  const failure = mutation.error?.failure ?? null;
  const controllable = props.canWrite && campaign.state !== 'ARCHIVED';

  function close(open: boolean): void {
    if (!open) {
      setDialog(null);
      mutation.reset();
    }
  }

  return (
    <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-1.5">
      <span
        aria-hidden
        className={cn(
          'size-2 shrink-0 rounded-full',
          campaign.state === 'ACTIVE' ? 'bg-primary' : 'bg-muted-foreground',
        )}
      />
      <span role="status" className="text-label text-foreground">
        {statusText(campaign)}
      </span>
      {controllable && (
        <>
          <Switch
            id={switchId}
            aria-label={rm('status.switch')}
            checked={campaign.state === 'ACTIVE'}
            disabled={mutation.isPending}
            onCheckedChange={(checked) => setDialog(checked ? 'resume' : 'pause')}
          />
          {campaign.state === 'PAUSED' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={rm('status.more')}
                >
                  <Ellipsis aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem variant="destructive" onSelect={() => setDialog('archive')}>
                  {rm('endCampaign.archive.trigger')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <ConfirmDialog
            open={dialog === 'pause'}
            onOpenChange={close}
            title={rm('status.pause.title')}
            description={rm('status.pause.description')}
            confirmLabel={rm('status.pause.confirm')}
            pending={mutation.isPending}
            failure={dialog === 'pause' ? failure : null}
            onConfirm={() => mutation.mutateAsync('PAUSED')}
          />
          <ConfirmDialog
            open={dialog === 'resume'}
            onOpenChange={close}
            title={rm('status.resume.title')}
            description={rm('status.resume.description')}
            confirmLabel={rm('status.resume.confirm')}
            pending={mutation.isPending}
            failure={dialog === 'resume' ? failure : null}
            onConfirm={() => mutation.mutateAsync('ACTIVE')}
          />
          <ConfirmDialog
            open={dialog === 'archive'}
            onOpenChange={close}
            title={rm('endCampaign.archive.title')}
            description={rm('endCampaign.archive.description')}
            confirmLabel={rm('endCampaign.archive.confirm')}
            destructive
            pending={mutation.isPending}
            failure={dialog === 'archive' ? failure : null}
            onConfirm={() => mutation.mutateAsync('ARCHIVED')}
          />
        </>
      )}
    </div>
  );
}
